import { useEffect, useState, type RefObject } from "react";
import { REDUCED_MOTION_QUERY, useMediaQuery } from "./useMotionPreferences";

export const VINYL_HOST_CLASS = "vinyl-host";

export const HOVERLESS_QUERY = "(hover: none)";

export const VINYL_VIEW_THRESHOLD = 0.6;

export type VinylPeek = "hover" | "always" | "view";

export type VinylRevealState = "rest" | "out";

export interface VinylRevealSignals {
  hovered: boolean;
  focused: boolean;
  seen: boolean;
  playing: boolean;
}

export function vinylRevealState(signals: VinylRevealSignals): VinylRevealState {
  return signals.playing || signals.hovered || signals.focused || signals.seen ? "out" : "rest";
}

export interface VinylMotion {
  restShift: number;
  outShift: number;
  restTurn: number;
  outTurn: number;
  restOpacity: number;
  outOpacity: number;
}

export function vinylTurnFor(shiftPercent: number): number {
  return Math.round(((shiftPercent / 100) * (360 / Math.PI)) * 1.6 * 10) / 10;
}

export function vinylMotion(shiftPercent: number, peek: VinylPeek, reduced: boolean): VinylMotion {
  const outShift = shiftPercent;
  if (reduced) {
    return { restShift: outShift, outShift, restTurn: 0, outTurn: 0, restOpacity: peek === "always" ? 1 : 0, outOpacity: 1 };
  }
  const restShift = peek === "always" ? Math.round(shiftPercent * 0.6 * 10) / 10 : 0;
  return { restShift, outShift, restTurn: vinylTurnFor(restShift), outTurn: vinylTurnFor(outShift), restOpacity: 1, outOpacity: 1 };
}

export function parseShiftPercent(shift: string): number {
  const value = Number.parseFloat(shift);
  return Number.isFinite(value) ? value : 0;
}

export function useVinylReveal(nodeRef: RefObject<HTMLElement | null>, options: { peek: VinylPeek; playing: boolean }) {
  const reduced = useMediaQuery(REDUCED_MOTION_QUERY);
  const hoverless = useMediaQuery(HOVERLESS_QUERY);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [seen, setSeen] = useState(false);
  const watchView = options.peek === "view" || hoverless;

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const host = (node.closest(`.${VINYL_HOST_CLASS}`) as HTMLElement | null) ?? node;
    const onEnter = (event: PointerEvent) => {
      if (event.pointerType === "touch") setSeen(true);
      else setHovered(true);
    };
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType !== "touch") setHovered(false);
    };
    const onDown = (event: PointerEvent) => {
      if (event.pointerType === "touch") setSeen(true);
    };
    const onFocusIn = () => setFocused(true);
    const onFocusOut = (event: FocusEvent) => {
      if (!host.contains(event.relatedTarget as Node | null)) setFocused(false);
    };
    host.addEventListener("pointerenter", onEnter);
    host.addEventListener("pointerleave", onLeave);
    host.addEventListener("pointerdown", onDown);
    host.addEventListener("focusin", onFocusIn);
    host.addEventListener("focusout", onFocusOut);
    return () => {
      host.removeEventListener("pointerenter", onEnter);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("pointerdown", onDown);
      host.removeEventListener("focusin", onFocusIn);
      host.removeEventListener("focusout", onFocusOut);
    };
  }, [nodeRef]);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !watchView || seen) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= VINYL_VIEW_THRESHOLD)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: [VINYL_VIEW_THRESHOLD] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [nodeRef, watchView, seen]);

  const state = vinylRevealState({ hovered, focused, seen: seen && watchView, playing: options.playing });
  return { state, reduced };
}
